import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { AREAS } from "@/lib/areas";
import { SERVICE_TOWNS_BY_AREA } from "@/lib/content";

export default function Areas() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Areas Covered — Declan Bishop Property Consultant",
    url: "https://dbpropertyconsultant.co.uk/areas",
    hasPart: AREAS.map((a) => ({
      "@type": "WebPage",
      name: `Property Surveyor in ${a.name}`,
      url: `https://dbpropertyconsultant.co.uk/areas/${a.slug}`,
    })),
  };

  return (
    <SiteLayout
      pageTitle="Areas Covered"
      pageDescription="Independent property surveyor covering the whole of Lincolnshire — Lincoln, Grantham, Sleaford, Boston, Skegness, Spalding, Louth, Bourne and 15 more towns. EPCs, surveys, snagging and landlord reports."
      pagePath="/areas"
    >
      <JsonLd id="areas-schema" data={schema} />
      <section data-testid="areas-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-16 pb-16 md:pt-24 md:pb-20">
          <div className="overline" data-testid="areas-overline">Areas covered</div>
          <h1
            data-testid="areas-headline"
            className="mt-6 font-serif text-[44px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight max-w-4xl"
          >
            Serving the whole of <span className="italic font-light text-brand-champagne">Lincolnshire.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">
            From the Cathedral Quarter in Lincoln to the coast at Skegness and the Fens around
            Spalding — local knowledge of the property stock in every corner of the county.
          </p>
        </div>
      </section>

      <section data-testid="areas-featured" className="bg-white border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline mb-10">Local area guides</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-navy/10 border border-brand-navy/10">
            {AREAS.map((a) => (
              <Link
                key={a.slug}
                to={`/areas/${a.slug}`}
                data-testid={`area-card-${a.slug}`}
                className="group bg-white p-8 md:p-10 hover:bg-brand-cream transition-colors"
              >
                <MapPin size={18} className="text-brand-champagne" />
                <h2 className="mt-5 font-serif text-2xl md:text-3xl text-brand-navy tracking-tight">
                  {a.name}
                </h2>
                <p className="mt-3 text-sm text-brand-navy/65 leading-relaxed line-clamp-3">
                  {a.intro}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-navy ink-link">
                  Surveys in {a.name}
                  <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="areas-full-list" className="bg-brand-cream">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline">Every town we serve</div>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy tracking-tight max-w-2xl">
            Wherever you are in the county, you&apos;re covered.
          </h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {SERVICE_TOWNS_BY_AREA.map((group) => (
              <div key={group.heading} data-testid={`areas-group-${group.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="text-[11px] tracking-[0.24em] uppercase text-brand-champagne font-semibold">
                  {group.heading}
                </div>
                <ul className="mt-5 space-y-2.5 text-sm text-brand-navy/80">
                  {group.towns.map((t) => {
                    const area = AREAS.find((a) => a.name === t);
                    return (
                      <li key={t}>
                        {area ? (
                          <Link to={`/areas/${area.slug}`} className="ink-link hover:text-brand-navy font-medium">
                            {t}
                          </Link>
                        ) : (
                          t
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-16">
            <Link
              to="/contact"
              data-testid="areas-cta"
              className="inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Request a Quote
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
